import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  EstadosImagenChoiceEnum,
  calcOTImages,
  solicitudUpdFotosSchema,
} from '@/shared';
import { useUpdateOrdenTrabajo } from '@/shared/actions/erp';
import { UpdFotosEntradaRequestData } from '@/shared/actions/erp/tecnico';
import { TabTextLabel } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { ImageListModal, ImageModal } from '@/shared/components/ui/Images';
import { OrdenTrabajo } from '@/shared/interfaces';
import { useAuthStore } from '@/store/auth';
import ConfirmUpdFotosAlertActivacionFotosModal from './ConfirmUpdFotosAlertActivacionFotosModal';

export type FotosEntradaSubidaOTModalProps = {
  open: boolean;
  onClose: () => void;
  ordenTrabajo: OrdenTrabajo;
};

type FormData = {
  comentario_solicita_upd_fotos: string;
  fecha_solicita_upd_fotos: string;
  usuario_solicita_upd_fotos: number;
};

const FotosEntradaSubidaOTModal: React.FC<FotosEntradaSubidaOTModalProps> = ({
  ordenTrabajo,
  open,
  onClose,
}) => {
  ///* local state ------------
  const [imagesArr, setImagesArr] = useState<ImageModal[]>([]);
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);

  ///* global state ------------
  const user = useAuthStore(s => s.user);

  ///* form ------------
  const form = useForm<FormData>({
    resolver: yupResolver(solicitudUpdFotosSchema) as any,
    defaultValues: {
      fecha_solicita_upd_fotos: dayjs().format(),
      usuario_solicita_upd_fotos: user?.user_id!,
    },
  });

  ///* mutations ------------
  const updOtFotosEntrada = useUpdateOrdenTrabajo<UpdFotosEntradaRequestData>({
    enableRedirect: false,
    customSuccessMessage:
      'Solicitud de actualización de fotos enviada con éxito',
  });

  ///* handlers ------------
  const handleConfirmUptPhotos = async (data: FormData) => {
    handleClose();

    updOtFotosEntrada.mutate({
      id: ordenTrabajo?.id_orden_trabajo!,
      data: {
        estado_imagen_entrada: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        estado_imagen_actualizacion: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        observaciones: {
          ...ordenTrabajo?.observaciones,
          observacion_foto_entrada_upd_sol: data.comentario_solicita_upd_fotos,
        },
      },
    });
  };

  const handleClose = () => {
    onClose();
    setIsOpenConfirmDialog(false);
  };

  ///* effects ------------
  useEffect(() => {
    if (!open) return;

    // antes era async
    const photesEntrada = calcOTImages(ordenTrabajo, 'urls_fotos_entrada');
    setImagesArr(photesEntrada);
  }, [open]);

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title="Fotos de Entrada"
        minWidth="54%"
        minHeight="30vh"
        contentNode={
          <>
            <Grid item xs={12} container spacing={3}>
              <>
                {/* ----- Image Modal ----- */}
                <Grid item xs={12}>
                  <TabTextLabel text="Fotos de Entrada" pt="0" pl="0" pb="0" />
                </Grid>
                <ImageListModal images={imagesArr || []} />
              </>
            </Grid>
          </>
        }
        onClose={handleClose}
        cancelTextBtn="Cerrar"
        // onConfirm to request new images
        onConfirm={() => {
          setIsOpenConfirmDialog(true);
        }}
        confirmTextBtn="Solicitar Actualización de Fotos"
        confirmBtnColor="error"
      />

      {/* ========= confirm dialog ========= */}
      <ConfirmUpdFotosAlertActivacionFotosModal
        form={form}
        handleConfirm={handleConfirmUptPhotos}
        isOpenConfirmDialog={isOpenConfirmDialog}
        setIsOpenConfirmDialog={setIsOpenConfirmDialog}
      />
    </>
  );
};

export default FotosEntradaSubidaOTModal;
